// SANlight Q4W Gen2 exterior interpretation, not manufacturer CAD. The user
// requested two Q4 lights; the generation was not specified. Only the envelope,
// four-module layout and pitch below are taken from the official drawing.
export const growLightReference = Object.freeze({
  model: "SANlight Q4W Gen2",
  article: "AA0105",
  source: "https://www.sanlight.com/wp-content/uploads/2021/07/datenblatt-q-serie-a4-05-002.pdf",
  sourcePage: 3,
  dimensionsMm: Object.freeze({ length: 555, width: 174, height: 119, modulePitch: 135 }),
  evidence: "manufacturer-envelope-with-illustrative-exterior-and-mounting",
  generation: "Gen2-assumed-for-presentation-not-installed-product-confirmation"
});

export const growLightLayout = Object.freeze({
  unitsPerMm: .0075,
  lowerFaceHeight: 5.65,
  suspensionHeight: 7.6,
  ceilingHeight: 10.05,
  serviceFeedZ: -8.25,
  stations: Object.freeze([-1.45, 1.45]),
  purpose: "presentation-only-no-PPFD-or-electrical-control"
});

export function createGrowLightFixtures(THREE) {
  const root = new THREE.Group(); root.name = "production-room-two-q4w-luminaires";
  root.userData = { presentationOnly: true, source: growLightReference.source, purpose: growLightLayout.purpose };
  const geometries = new Set(), materials = new Set();
  const surface = (color, options = {}) => {
    const result = new THREE.MeshStandardMaterial({ color, roughness: .34, metalness: .75, ...options });
    materials.add(result); return result;
  };
  const aluminium = surface(0xc7ced1), finEdges = surface(0xcfd5d6, { roughness: .27 });
  const powderCoat = surface(0xe8e9e4, { roughness: .44, metalness: .25 });
  const dark = surface(0x202523, { roughness: .69, metalness: .02 });
  const steel = surface(0xb8c4c7, { roughness: .26, metalness: .95 });
  const optic = surface(0xfff7dc, { roughness: .3, metalness: 0, emissive: 0xffedc9, emissiveIntensity: .38 });
  const glass = surface(0xf3f6ee, { roughness: .18, metalness: .02 });
  const mesh = (name, geometry, material, parent, position = [0, 0, 0]) => {
    geometries.add(geometry);
    const object = new THREE.Mesh(geometry, material); object.name = name;
    object.position.set(...position); object.castShadow = material !== optic;
    object.receiveShadow = true; parent.add(object); return object;
  };
  const box = (name, size, material, parent, position) => mesh(name, new THREE.BoxGeometry(...size), material, parent, position);
  const line = (name, points, radius, material, parent) => {
    const curve = new THREE.CatmullRomCurve3(points.map(point => new THREE.Vector3(...point)), false, "centripetal");
    const object = mesh(name, new THREE.TubeGeometry(curve, points.length * 5, radius, 6, false), material, parent);
    object.userData.presentationCable = true; return object;
  };
  const u = growLightLayout.unitsPerMm, dims = growLightReference.dimensionsMm;
  const length = dims.length * u, width = dims.width * u, height = dims.height * u;
  const pitch = dims.modulePitch * u;
  const moduleCenters = [-1.5, -.5, .5, 1.5].map(value => value * pitch);
  const fixtures = growLightLayout.stations.map((z, fixtureIndex) => {
    const fixture = new THREE.Group(); fixture.name = `sanlight-q4w-${fixtureIndex + 1}`;
    fixture.position.set(0, growLightLayout.lowerFaceHeight, z);
    fixture.userData = { model: growLightReference.model, evidence: growLightReference.evidence, schematicPlacement: true, noPhotometricCalculation: true };
    root.add(fixture);
    const body = new THREE.Group(); body.name = `${fixture.name}-body`; fixture.add(body);
    // Tall central driver rail and the four independently finned LED modules.
    box("q4w-central-driver-rail", [514 * u, height - .12, .29], powderCoat, body, [0, height / 2, 0]);
    box("q4w-rail-upper-ridge", [514 * u, .035, .34], aluminium, body, [0, height - .055, 0]);
    [-1, 1].forEach(side => {
      box(`q4w-rail-side-groove-${side}`, [508 * u, .018, .016], dark, body, [0, height * .72, side * .15]);
    });
    const modules = moduleCenters.map((x, moduleIndex) => {
      const module = new THREE.Group(); module.name = `q4w-optical-module-${moduleIndex + 1}`;
      module.position.x = x; body.add(module);
      box("q4w-module-heatsink-base", [.65, .07, width], aluminium, module, [0, .095, 0]);
      // These are finite raised plates, not a normal-map imitation of fins.
      const fins = Array.from({ length: 12 }, (_, fin) => {
        const finZ = -width / 2 + .017 + fin * (width - .034) / 11;
        const finHeight = .39 + .04 * (1 - Math.abs(finZ) / (width / 2));
        return box(`q4w-cooling-fin-${fin + 1}`, [.65, finHeight, .022], finEdges, module, [0, .13 + finHeight / 2, finZ]);
      });
      box("q4w-optics-black-seat", [.61, .035, width - .065], dark, module, [0, .043, 0]);
      const lensFrame = box("q4w-optics-clear-rim", [.575, .026, width - .105], glass, module, [0, .016, 0]);
      // Small lens banks match the modular appearance. These are illustrative
      // emitters, not a claimed count/shape of LED dies or a calibrated beam.
      const lenses = [-1, 1].map(side => box(`q4w-warm-white-optic-${side}`, [.495, .008, (width - .22) / 2], optic, module, [0, .004, side * (width - .15) / 4]));
      return { group: module, fins, lensFrame, lenses };
    });
    const mountX = 247 * u;
    const suspension = [-1, 1].map(side => {
      const x = side * mountX;
      box(`q4w-end-bracket-${side}`, [.045, height - .035, .31], aluminium, body, [x, height / 2, 0]);
      const connector = mesh(`q4w-end-connector-${side}`, new THREE.CylinderGeometry(.082, .082, .17, 12), dark, body, [side * (length / 2 - .085), .42, 0]);
      connector.rotation.z = Math.PI / 2;
      const eye = mesh(`q4w-suspension-eye-${side}`, new THREE.TorusGeometry(.038, .009, 6, 12), steel, body, [x, height - .047, 0]);
      const suspensionTop = growLightLayout.suspensionHeight - growLightLayout.lowerFaceHeight;
      const cable = line(`q4w-suspension-wire-${side}`, [[x, height - .01, 0], [x, suspensionTop, 0]], .009, steel, fixture);
      box(`q4w-cable-height-adjuster-${side}`, [.044, .10, .044], aluminium, fixture, [x, height + .12, 0]);
      return { eye, cable, top: new THREE.Vector3(x, growLightLayout.suspensionHeight, z) };
    });
    // Keep both feeds overhead and outside the plants; the source terminates
    // on the presentation's rear service rail, never on the FEEDER controller.
    const feed = line("q4w-overhead-power-feed", [[length / 2, .42, 0], [length / 2 + .16, .6, 0], [length / 2 + .2, 1.25, 0], [length / 2 + .2, 1.72, -.35], [length / 2 + .2, 1.72, growLightLayout.serviceFeedZ - z]], .022, dark, fixture);
    const railY = growLightLayout.suspensionHeight - growLightLayout.lowerFaceHeight;
    const suspensionRail = box("q4w-ceiling-suspension-rail", [4.5, .09, .11], aluminium, fixture, [0, railY + .045, 0]);
    const ceilingY = growLightLayout.ceilingHeight - growLightLayout.lowerFaceHeight;
    const ceilingRods = [-1, 1].map(side => {
      const rod = mesh(`q4w-ceiling-support-rod-${side}`, new THREE.CylinderGeometry(.018, .018, ceilingY - railY - .09, 8), steel, fixture, [side * 2.12, (ceilingY + railY + .09) / 2, 0]);
      const plate = box(`q4w-ceiling-anchor-plate-${side}`, [.16, .025, .16], aluminium, fixture, [side * 2.12, ceilingY - .0125, 0]);
      return { rod, plate };
    });
    return { root: fixture, body, modules, suspension, suspensionRail, ceilingRods, feed, dimensions: { length, width, height } };
  });
  root.updateMatrixWorld(true);
  let disposed = false;
  return { root, fixtures, reference: growLightReference, layout: growLightLayout, geometries, materials,
    dispose() { if (disposed) return; disposed = true; geometries.forEach(item => item.dispose()); materials.forEach(item => item.dispose()); }
  };
}
